import React from "react";
import { IconButton } from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import { ModeEditOutlineTwoTone, InfoOutlined } from "@mui/icons-material";
import { useAuth, useTablePagination } from "hooks";
import { usePaymentAccountDetailContext } from "contexts/PaymentContexts/PaymentAccountContext";
import HighlightedText from "components/Shared/HighlightedText";
import OpenInNewTab from "components/Shared/OpenInNewTab";
import { getLedgerSourceInfo } from "utils/accountLedgerformatValue";
import { formatAmount } from "utils/formatString";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { useLazyListAccountLedgerQuery } from "store/services/accountLedgerApi";
import AppTable from "components/Shared/AppTable";

export const PaymentAccountLedgerTab = () => {
  const [searchParams] = useSearchParams();
  const { isEntityAdmin } = useAuth();

  const { paymentAccountDetail } = usePaymentAccountDetailContext();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";
  const sortBy = searchParams.get("sortBy") || "createdAt";
  const sortDirection = searchParams.get("sortDirection") || -1;

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const columns = [
    {
      name: "Payment Account",
      selector: (row) => row?.paymentAccount?.accountName,

      cell: (row) => (
        <Link
          to={`/app/crm/account-ledger/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-500 hover:underline"
        >
          {row?.paymentAccount?.accountName ? (
            <HighlightedText
              text={`${row?.paymentAccount?.accountName}`}
              highlight={keyword}
            />
          ) : (
            "N/A"
          )}
        </Link>
      ),
      grow: 2,
    },
    ...(!isEntityAdmin
      ? [
          {
            name: "Entity",
            cell: (row) => (
              <OpenInNewTab
                label={row?.entity?.displayName ?? row?.entity?.name}
                link={`/app/crm/entity/${row?.entity?._id}/detail`}
                disableIcon
              />
            ),
          },
        ]
      : []),
    {
      name: "Vendor",
      selector: (row) => (
        <OpenInNewTab
          label={row?.vendor?.vendor?.name}
          link={`/app/crm/vendor-profile/${row?.vendor?._id}/detail`}
          disableIcon
        />
      ),
      align: "center",
    },

    {
      name: "Original Source",
      selector: (row) => {
        const sourceInfo = getLedgerSourceInfo(
          row?.originalSource,
          row?.originalSourceRef
        );
        return (
          <div className="">
            {" "}
            <OpenInNewTab
              label={sourceInfo?.label}
              link={`/app/crm/${sourceInfo?.link}/${sourceInfo?._id}/detail`}
              disableIcon
            />
          </div>
        );
      },
      align: "center",
    },

    {
      name: "Added By",
      selector: (row) => (
        <OpenInNewTab
          label={row?.addedBy?.name}
          link={`/app/crm/user/${row?.addedBy?._id}/detail`}
          disableIcon
        />
      ),
      align: "center",
    },

    {
      name: "Amount",
      selector: (row) =>
        `${row?.entity?.currency?.code} ${formatAmount(row?.amount)}`,
      align: "center",
    },
    {
      name: "Transaction Date",
      selector: (row) =>
        <AppTableDateTime date={row?.transactionDate} dateOnly /> ?? "N/A",
      align: "center",
    },

    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/account-ledger/${row._id}/detail`}>
            <IconButton color="primary" aria-label="Edit Payment Account">
              <InfoOutlined />
            </IconButton>
          </Link>

          <Link to={`/app/crm/account-ledger/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit Payment Account">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
      maxWidth: "100px",
    },
  ];
  // RTK Query
  const [
    listAccountLedger,
    {
      data: accountLedgerRes,
      isLoading: loadingAccountLedger,
      isFetching: fetchingAccountLedger,
    },
  ] = useLazyListAccountLedgerQuery();

  const isBusy = loadingAccountLedger || fetchingAccountLedger;

  React.useEffect(() => {
    listAccountLedger({
      page: currentPage,
      limit: perPage,
      sortBy,
      sortDirection,
      ...(keyword && { keyword }),
      ...(paymentAccountDetail?._id && {
        paymentAccount: paymentAccountDetail?._id,
      }),
      status: activeTab === "Active" ? "Active" : "Inactive",
    });
  }, [
    activeTab,
    currentPage,
    keyword,
    listAccountLedger,
    paymentAccountDetail,
    perPage,
    sortBy,
    sortDirection,
  ]);

  return (
    <>
      <AppTable
        columns={columns}
        data={accountLedgerRes?.data ?? []}
        progressPending={isBusy}
        paginationServer
        paginationPerPage={perPage}
        paginationTotalRows={accountLedgerRes?.totalItems ?? 0}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
      />
    </>
  );
};
